// BookArrivedPage — "did your book arrive?" (thread mct_bb04f0ef-ceb).
//
//   /books/arrived/:token?a=got_it|not_yet   — the two buttons in the check-in email
//
// The signed token binds one parcel and the recipient's email; the worker
// (books-public-routes.ts → books-arrival.ts) refuses anything else. The page
// asks for one more tap before it records anything: mail scanners open every
// link in an email, and a scanner must never mark a parcel delivered or raise
// a chase task. "Got it" marks the parcel delivered; "Not yet" tells the team,
// with an optional note. Either answer can be changed later from the same link.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

type Answer = 'got_it' | 'not_yet';

interface PageData {
  firstName: string | null;
  titles: string[];
  shippedAt: string | null;
  answer: Answer | null;
  answeredAt: string | null;
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.12, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: '2px solid #F05A28', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginRight: 10, marginBottom: 10 };
const btnOutline: CSSProperties = { ...btn, background: '#fff', color: '#F05A28' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', minHeight: 90, fontFamily: 'inherit' };
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };
const linkBtn: CSSProperties = { background: 'none', border: 'none', padding: 0, color: '#F05A28', fontWeight: 600, cursor: 'pointer', fontSize: 14 };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

function titlesWords(titles: string[]): string {
  if (titles.length === 0) return 'your book';
  if (titles.length === 1) return `your copy of ${titles[0]}`;
  return `your books, ${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
}

export function BookArrivedPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const fromLink = params.get('a');
  const [data, setData] = useState<PageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'missing' | 'error'>('loading');
  const [choice, setChoice] = useState<Answer | null>(fromLink === 'got_it' || fromLink === 'not_yet' ? fromLink : null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/books/arrival/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 401) { setState('invalid'); return; }
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        setData((await r.json()) as PageData);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [token]);

  const send = async (answer: Answer) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/books/arrival/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer, note: answer === 'not_yet' ? note : undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}).`); return; }
      setData(d.page as PageData);
      setChanging(false);
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'invalid') {
    return (
      <Shell>
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>No problem. Just reply to the email we sent you and let us know whether your book arrived.</p>
      </Shell>
    );
  }
  if (state === 'missing' || state === 'error' || !data) {
    return (
      <Shell>
        <h1 style={h1}>{state === 'missing' ? 'We could not find this parcel' : 'Something went wrong'}</h1>
        <p style={lead}>Please try the link again in a moment, or reply to the email we sent you and we will help.</p>
      </Shell>
    );
  }

  const hello = data.firstName ? `Hi ${data.firstName},` : 'Hi there,';
  const what = titlesWords(data.titles);

  if (data.answer && !changing) {
    return (
      <Shell>
        {data.answer === 'got_it' ? (
          <>
            <h1 style={h1}>Wonderful, thank you</h1>
            <p style={lead}>{hello} we're glad {what} made it. We hope you enjoy reading it.</p>
          </>
        ) : (
          <>
            <h1 style={h1}>Thank you for telling us</h1>
            <p style={lead}>{hello} someone on our team will look into where {what} has got to and email you, usually within one business day. If we can't find it, we'll send you another copy.</p>
          </>
        )}
        <p style={small}>
          {data.answer === 'got_it' ? 'Tapped the wrong button?' : 'Has it turned up since?'}{' '}
          <button type="button" style={linkBtn} onClick={() => { setChanging(true); setChoice(data.answer === 'got_it' ? 'not_yet' : 'got_it'); }}>Change my answer</button>.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={h1}>Did your book arrive?</h1>
      <p style={lead}>{hello} please confirm whether {what} has reached you.</p>
      <div style={card}>
        <button type="button" style={choice === 'got_it' ? btn : btnOutline} disabled={busy} onClick={() => setChoice('got_it')}>Got it</button>
        <button type="button" style={choice === 'not_yet' ? btn : btnOutline} disabled={busy} onClick={() => setChoice('not_yet')}>Not yet</button>

        {choice === 'not_yet' && (
          <div style={{ marginTop: 14 }}>
            <label htmlFor="arr-note" style={{ display: 'block', fontWeight: 600, fontSize: 15, margin: '0 0 6px', color: '#0A0A2E' }}>
              Anything we should know? (optional)
            </label>
            <textarea id="arr-note" style={input} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="For example, if you have moved since you ordered" />
          </div>
        )}

        {choice && (
          <div style={{ marginTop: 18 }}>
            <button type="button" style={btn} disabled={busy} onClick={() => send(choice)}>
              {busy ? 'Sending…' : choice === 'got_it' ? 'Yes, it arrived' : 'Tell the team it has not arrived'}
            </button>
          </div>
        )}
        {err && <p style={{ ...small, color: '#b42318', marginTop: 12 }}>{err}</p>}
      </div>
    </Shell>
  );
}
